import { useCallback, useState } from 'react';

/**
 * Day / Night reading mode for database-driven articles (Asana 1219952231021031).
 * Per browser, not per account: the choice lives in localStorage and the first
 * visit follows the OS colour scheme. Read synchronously in the useState
 * initializer so the first painted frame already carries the right theme.
 */
export type ArticleTheme = 'day' | 'night';

export const ARTICLE_THEME_STORAGE_KEY = 'tm-article-theme';

const isArticleTheme = (value: unknown): value is ArticleTheme => value === 'day' || value === 'night';

export const readStoredArticleTheme = (): ArticleTheme => {
  if (typeof window === 'undefined') return 'night';
  try {
    const stored = window.localStorage.getItem(ARTICLE_THEME_STORAGE_KEY);
    if (isArticleTheme(stored)) return stored;
  } catch {
    // localStorage unavailable: fall through to the OS preference.
  }
  try {
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'day' : 'night';
  } catch {
    return 'night';
  }
};

export function useArticleTheme(): [ArticleTheme, (theme: ArticleTheme) => void] {
  const [theme, setThemeState] = useState<ArticleTheme>(readStoredArticleTheme);

  const setTheme = useCallback((next: ArticleTheme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(ARTICLE_THEME_STORAGE_KEY, next);
    } catch {
      // Storage unavailable: the choice lasts for this page only.
    }
  }, []);

  return [theme, setTheme];
}
